import React, { useEffect, useRef, useState } from "react";
import * as Navigation from "../../../../../specific/utils/Navigation/Navigation";

import {
    Image,
    Linking,
    Platform,
    ScrollView,
    Text,
    TouchableOpacity,
    View
} from "react-native";
import AppTheme from "../../../../utils/Theme";
import Images from "../../../../../specific/utils/Images";
import TextInput from "../../../../designSystem/TextInput/RoundCornerTextInput";
import Divider from "../../../../designSystem/Divider/Divider";
import RoundButton from "../../../../designSystem/Buttons/RoundButton";
import LinkButton from "../../../../designSystem/Buttons/LinkButton";
import i18n from "../../../../utils/Localization/Localization";
import { validateEmail } from "../../../../utils/StringUtils";
import { createUserWithEmailAndPassword } from "../../../../services/Auth";
import ComponentProps from "../../../../../specific/utils/Interfaces/ComponentProps";
import {  useNavigate } from "../../../../../specific/utils/Navigation/Navigation";
import RadioButton from "../../../../designSystem/RadioButton";
import LandingStyle from "../../../../styles/auth/LandingStyle";
import TeacherSignUpStyle from "../../../../styles/components/TeacherSignUpStyle";
import { CONTEXT, ScreenCookiesPolicy } from "../../../../utils/Constants";
import Alert from "../../../Notification/Alert";
import User from "../../../../data/user/User";
import { addContactToGeneralAudience } from "../../../../services/Mailing";
import ActionSheet from "../../../../designSystem/ActionSheet/ActionSheet";
import SVGView from "../../../../utils/SvgView";
import { default as IcSearch } from "../../../../assets/svg/lili/ic_search.svg";
import { setUserProperty } from "../../../../services/Tracking/Analytics";
import { getSchoolId } from "../../../../services/Firebase/Database";

interface TeacherSignUpProps extends ComponentProps {
    onCancelRequest: () => void,
    onSignUpConfirmed: () => void,
    userEmail?: string
}

type Errors = {
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    schoolName: string,
    schoolAddress: string,
    schoolZipCode: string,
    schoolCity: string,
    schoolCountry: string
}

const emptyErrors: Errors = {
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    schoolName: "",
    schoolAddress: "",
    schoolZipCode: "",
    schoolCity: "",
    schoolCountry: ""
};

type Etablissement = {
    identifiant_de_l_etablissement: string;
    nom_etablissement: string;
    type_etablissement: string;
    statut_public_prive: string;
    adresse_1: string;
    adresse_2: string | null;
    adresse_3: string;
    code_postal: string;
    code_commune: string;
    nom_commune: string;
    code_departement: string;
    code_academie: string;
    code_region: string;
    ecole_maternelle: number;
    ecole_elementaire: number;
    voie_generale: number | null;
    voie_technologique: number | null;
    voie_professionnelle: number | null;
    telephone: string;
    fax: string | null;
    web: string | null;
    mail: string;
    restauration: number;
    hebergement: number;
    ulis: number;
    apprentissage: number | null;
    segpa: number | null;
    section_arts: number | null;
    section_cinema: number | null;
    section_theatre: number | null;
    section_sport: number | null;
    section_internationale: number | null;
    section_europeenne: number | null;
    lycee_agricole: number | null;
    lycee_militaire: number | null;
    lycee_des_metiers: number | null;
    post_bac: number | null;
    appartenance_education_prioritaire: number | null;
    greta: number | null;
    siren_siret: string;
    nombre_d_eleves: number;
    fiche_onisep: string | null;
    position: {
        lon: number;
        lat: number;
    };
    type_contrat_prive: string;
    libelle_departement: string;
    libelle_academie: string;
    libelle_region: string;
    coordx_origine: number;
    coordy_origine: number;
    epsg_origine: string;
    nom_circonscription: string;
    latitude: number;
    longitude: number;
    precision_localisation: string;
    date_ouverture: string; // Format YYYY-MM-DD
    date_maj_ligne: string; // Format YYYY-MM-DD
    etat: string;
    ministere_tutelle: string;
    multi_uai: number;
    rpi_concentre: number;
    rpi_disperse: number | null;
    code_nature: number;
    libelle_nature: string;
    code_type_contrat_prive: string;
    pial: string;
    etablissement_mere: string | null;
    type_rattachement_etablissement_mere: string | null;
    code_circonscription: string;
    code_zone_animation_pedagogique: string | null;
    libelle_zone_animation_pedagogique: string | null;
    code_bassin_formation: string;
    libelle_bassin_formation: string;
};

export type School = {
    uai?: string;
    name: string;
    type?: string;
    is_public?: boolean;
    address: string;
    address_comp?: string;
    zipcode: string;
    city: string;
    department_code?: string;
    academy_code?: string;
    region_code?: string;
    phone_number?: string;
    web?: string,
    email?: string,
    kindergarten?: boolean;
    elementary?: boolean;
    ulis?: boolean;
    segpa?: boolean;
    highschool?: boolean;
    prioritary_eduction?: boolean;
    greta?: boolean;
    department_name?: string;
    academy_name?: string;
    region_name?: string;
    school_nature_code?: number;
    school_nature_name?: string;
    pial?: string;
    country: string;
}

const establishmentToSchoolDirectLinks = {
    identifiant_de_l_etablissement: "uai",
    nom_etablissement: "name",
    type_etablissement: "type",
    adresse_1: "address",
    adresse_2: "address_comp",
    code_postal: "zipcode",
    nom_commune: "city",
    code_departement: "department_code",
    code_academie: "academy_code",
    code_region: "region_code",
    libelle_departement: "department_name",
    libelle_academie: "academy_name",
    libelle_region: "region_name",
    code_nature: "school_nature_code",
    libelle_nature: "school_nature_name",
    pial: "pial"
}

const establishmentKeysWithNumberValueToBoolean = {
    ecole_maternelle: "kindergarten",
    ecole_elementaire: "elementary",
    ulis: "ulis",
    segpa: "segpa",
    lycee_agricole: "highschool",
    lycee_militaire: "highschool",
    lycee_des_metiers: "highschool",
    appartenance_education_prioritaire: "prioritary_eduction", // rep
    greta: "greta"
}

const etablissementToSchoolMigration = (establishment: Etablissement, country?: string) => {
    let schoolData: School = {
        name: "",
        address: "",
        zipcode: "",
        city: "",
        country: country !== undefined ? country : ""
    };
    for (const anEstablishmentKey in establishment) {
        if (Object.prototype.hasOwnProperty.call(establishment, anEstablishmentKey)) {
            const anEstablishmentValue = establishment[anEstablishmentKey];
            if ((anEstablishmentValue !== undefined) && (anEstablishmentValue !== null)) {
                if (establishmentToSchoolDirectLinks[anEstablishmentKey] !== undefined) {
                    schoolData[establishmentToSchoolDirectLinks[anEstablishmentKey]] = anEstablishmentValue;
                } else if (establishmentKeysWithNumberValueToBoolean[anEstablishmentKey] !== undefined) {
                    schoolData[establishmentKeysWithNumberValueToBoolean[anEstablishmentKey]] = anEstablishmentValue === 1 ? true : false;
                } else if (anEstablishmentKey === "statut_public_prive") {
                    // On peut avoir 2 valeurs Public ou Privée
                    if (anEstablishmentValue === "Public") {
                        schoolData.is_public = true;
                    } else {
                        schoolData.is_public = false;
                    }
                } 
            }
        }
    }
    return schoolData;
}

const TeacherSignUp = (props: TeacherSignUpProps) => {

    const appTheme: AppTheme = new AppTheme();
    const landingStyle: LandingStyle = new LandingStyle();
    const teacherSignUpStyle: TeacherSignUpStyle = new TeacherSignUpStyle();
    const images: Images = new Images();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<Errors | undefined>(undefined);

    const alertRef = useRef<Alert>(null);
    const actionSheetRef = useRef<ActionSheet>(null);
    const signUpPagerRef = useRef<ScrollView>(null);

    const { componentId = "", userEmail = "", onCancelRequest, onSignUpConfirmed } = props;
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [firstname, setFirstname] = useState<string>("");
    const [lastname, setLastname] = useState<string>("");
    const [newsletter, setNewsletter] = useState<boolean>(false);
    const [teacherFunction, setTeacherFunction] = useState<string | undefined>(undefined);
    const [school, setSchool] = useState<School | undefined>(undefined);
    const [schoolName, setSchoolName] = useState<string>("");
    const [schoolAddress, setSchoolAddress] = useState<string>("");
    const [schoolZipCode, setSchoolZipCode] = useState<string>("");
    const [schoolCity, setSchoolCity] = useState<string>("");
    const [schoolCountry, setSchoolCountry] = useState<string>("");
    const [schoolType, setSchoolType] = useState<"public" | "private" | "unknown">("unknown");
    const [schoolId, setSchoolId] = useState<string>("");
    const [schoolSelected, setSchoolSelected] = useState<boolean>(false);
    const [step, setStep] = useState(0);
    const [classes, setClasses] = useState<string[]>([]);

    const scrollViewWidth = appTheme.getBlockWidth();

    let navigate:  any = "";
    if (Platform.OS === "web") {
        navigate = useNavigate();
    }

    useEffect(() => {
        signUpPagerRef.current?.scrollTo({ x: scrollViewWidth * step, y: 0, animated: true });
    }, [step]);

    useEffect(() => {
        if (schoolId.length > 0) {
            goToStepAfterSchoolSelection();
        }
    }, [schoolId]);

    const goToStepAfterSchoolSelection = () => {
        // On va regarder quelle fonction a l'utilisateur pour savoir si on lui demande des précisions
        if ((teacherFunction === "teacher") || (teacherFunction === "establishment_head")) {
            setStep(2);
        } else {
            onThirdStepValidation();
        }
    }

    const onSecondStepValidation = async () => {
        setIsLoading(true);
        let signUpErrors = emptyErrors;
        if (schoolName.length === 0) {
            signUpErrors.schoolName = i18n.t("authentication.signUp.errors.empty_organisation_name");
        } else {
            signUpErrors.schoolName = "";
        }
        if (schoolAddress.length === 0) {
            signUpErrors.schoolAddress = i18n.t("authentication.signUp.errors.empty_organisation_address");
        } else {
            signUpErrors.schoolAddress = "";
        }
        if (schoolZipCode.length === 0) {
            signUpErrors.schoolZipCode = i18n.t("authentication.signUp.errors.empty_organisation_zip");
        } else {
            signUpErrors.schoolZipCode = "";
        }
        if (schoolCity.length === 0) {
            signUpErrors.schoolCity = i18n.t("authentication.signUp.errors.empty_organisation_city");
        } else {
            signUpErrors.schoolCity = "";
        }
        if (schoolCountry.length === 0) {
            signUpErrors.schoolCountry = i18n.t("authentication.signUp.errors.empty_organisation_country");
        } else {
            signUpErrors.schoolCountry = "";
        }
        if ((signUpErrors.schoolName.length > 0) || (signUpErrors.schoolAddress.length > 0) || (signUpErrors.schoolZipCode.length > 0) || (signUpErrors.schoolCity.length > 0) || (signUpErrors.schoolCountry.length > 0)) {
            setErrors(signUpErrors);
        } else {
            // On va récupérer l'ID de l'école
            try {
                let schoolToLookFor: School = {
                    name: schoolName,
                    address: schoolAddress,
                    zipcode: schoolZipCode,
                    city: schoolCity,
                    country: schoolCountry
                }
                if (school !== undefined) {
                    schoolToLookFor = school;
                }
                const userSchoolId = await getSchoolId({school: schoolToLookFor});
                setSchoolId(userSchoolId);
            } catch (error) {
                setSchoolId("unknown");
            }
        }
        setIsLoading(false);
    }

    const isUserTneEnable = () => {
        const TNEDiscrict = {
            "02": "dans l'Aisne",
            "2": "dans l'Aisne",
            "13": "dans les Bouches-du-Rhône",
            "18": "dans le Cher",
            "2A": "en Corse-du-Sud",
            "25": "dans le Doubs",
            "29": "dans le Finistère",
            "971": "en Guadeloupe",
            "34": "dans l'Hérault",
            "38": "dans l'Isère",
            "95": "dans le Val-d'Oise",
            "86": "dans la Vienne",
            "88": "dans les Vosges"
        }

        const districtNumbers = Object.keys(TNEDiscrict);
        // On va récupérer le numéro de département du zip code
        let zipNumber = "";
        const schoolZipCodeNoBlank: string = schoolZipCode.trim();
        if (schoolZipCodeNoBlank.length === 4) {
            zipNumber = schoolZipCodeNoBlank.substring(0, 1);
        } else if (schoolZipCodeNoBlank.length === 5) {
            zipNumber = schoolZipCodeNoBlank.substring(0, 2);
            if (zipNumber === "97") {
                zipNumber = schoolZipCodeNoBlank.substring(0, 3);
            }
        }
        if (districtNumbers.indexOf(zipNumber) !== -1) {
            return true;
        }
        return false;
    }

    // Condition pour afficher le bouton permettant de basculer vers la connexion
    const getStepIndicatorByStep = () => {
        if (step === 3) {
            return <View />;
        }
        let stepsBackgroundColors = [appTheme.schoolColor, appTheme.white, appTheme.white];
        let stepsColors = [appTheme.white, appTheme.schoolColor, appTheme.schoolColor];
        if ((step === 1) || (step === 2)) {
            stepsBackgroundColors = [appTheme.schoolColor, appTheme.schoolColor, appTheme.white];
            stepsColors = [appTheme.white, appTheme.white, appTheme.schoolColor];
        } else if (step === 3) {
            stepsBackgroundColors = [appTheme.schoolColor, appTheme.schoolColor, appTheme.schoolColor];
            stepsColors = [appTheme.white, appTheme.white, appTheme.white];
        }
        return <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <View style={[teacherSignUpStyle.positionIndicatorCircleStyle, { backgroundColor: stepsBackgroundColors[0], borderColor: appTheme.schoolColor }]}>
                <Text style={[teacherSignUpStyle.positionIndicatorTextStyle, { fontFamily: appTheme.primaryFont, color: stepsColors[0] }]}>1</Text>
            </View>
            <View style={{ width: appTheme.pixelPerfect(20), height: 1, backgroundColor: appTheme.schoolColor }} />
            <View style={[teacherSignUpStyle.positionIndicatorCircleStyle, { backgroundColor: stepsBackgroundColors[1], borderColor: appTheme.schoolColor }]}>
                <Text style={[teacherSignUpStyle.positionIndicatorTextStyle, { fontFamily: appTheme.primaryFont, color: stepsColors[1] }]}>2</Text>
            </View>
            <View style={{ width: appTheme.pixelPerfect(20), height: 1, backgroundColor: appTheme.schoolColor }} />
            <View style={[teacherSignUpStyle.positionIndicatorCircleStyle, { backgroundColor: stepsBackgroundColors[2], borderColor: appTheme.schoolColor }]}>
                <Text style={[teacherSignUpStyle.positionIndicatorTextStyle, { fontFamily: appTheme.primaryFont, color: stepsColors[2] }]}>3</Text>
            </View>
        </View>
    }

    const showSchoolSearch = () => {
        actionSheetRef.current.showAlert({
            actionSheetType: "SCHOOLSEARCH"
        });
    }

    const selectSchool = (item: Etablissement) => {
        const schoolDataForFB: School = etablissementToSchoolMigration(item, "France");
        setSchool(schoolDataForFB);
        setSchoolName(item.nom_etablissement);
        setSchoolAddress(item.adresse_1);
        setSchoolZipCode(item.code_postal);
        setSchoolCity(item.nom_commune);
        setSchoolCountry("France");
        let schoolStatus: "public" | "private" | "unknown" = item.statut_public_prive === "Public" ? "public" : item.statut_public_prive === "Privé" ? "private" : "unknown";
        setSchoolType(schoolStatus);
        setSchoolSelected(true);
        actionSheetRef.current.hideAlert();
    }

    const toggleClasses = (value: string) => {
        let teacherClasses = classes;
        const valueIndex = teacherClasses.indexOf(value);
        if (valueIndex !== -1) {
            teacherClasses.splice(valueIndex, 1);
        } else {
            teacherClasses.push(value);
        }
        setClasses(teacherClasses);
    }

    const openGeneralConditions = () => {
        if (Platform.OS === "web") {
            let uri = "https://www.lili.cool/fr/mentions-legales/";
            Linking.openURL(uri);
        } else {
            Navigation.showModalInView({
                componentId,
                name: ScreenCookiesPolicy,
                passProps: {
                    uri: "https://www.lili.cool/fr/mentions-legales/"
                },
                options: {
                    modalPresentationStyle: 'overCurrentContext',
                    layout: {
                        componentBackgroundColor: 'transparent'
                    }
                }
            });
        }
    }

    const onThirdStepValidation = async () => {
        try {
            setIsLoading(true);
            const emailToRecord = email.toLowerCase().trim();
            const tneEnable = isUserTneEnable();
            const userSession: User | undefined = await createUserWithEmailAndPassword({
                context: CONTEXT.teacher,
                email: emailToRecord,
                password,
                options: {
                    firstname,
                    lastname,
                    optInCoachNewsLetter: false,
                    optInNewsletter: newsletter,
                    school_id: schoolId,
                    classes,
                    tneEnable,
                    function: teacherFunction
                }
            });
            let isDirector: boolean = false;

            if (teacherFunction === "establishment_head") {
                setUserProperty({ key: "school_principal", value: "true" });
                isDirector = true;
            } else {
                setUserProperty({ key: "school_principal", value: "false" });
            }
            if (classes.length === 1) {
                if (isDirector === false) {
                    setUserProperty({ key: "school_level", value: classes[0] });
                }
            } else if (classes.length > 1) {
                if (isDirector === false) {
                    setUserProperty({ key: "school_level", value: "several" });
                } else if (classes.length === 2) {
                    let schoolLevel: string = classes[0];
                    if (classes[0] === "direction") {
                        schoolLevel = classes[1];
                    }
                    setUserProperty({ key: "school_level", value: schoolLevel });
                } else {
                    setUserProperty({ key: "school_level", value: "several" });
                }
            }
            setUserProperty({ key: "rare_emails_optin", value: newsletter.toString(), once: true });
            if (schoolId.length > 0) {
                setUserProperty({ key: "school_id", value: schoolId })
            }
            if (schoolName.length > 0) {
                setUserProperty({ key: "school_name", value: schoolName })
            }
            if (schoolAddress.length > 0) {
                setUserProperty({ key: "school_address", value: schoolAddress })
            }

            const schoolZipCodeNoBlank: string = schoolZipCode.trim();
            if (schoolZipCodeNoBlank.length > 0) {
                let zipNumber = "";
                if (schoolZipCodeNoBlank.length === 4) {
                    zipNumber = schoolZipCodeNoBlank.substring(0, 1);
                } else if (schoolZipCodeNoBlank.length === 5) {
                    zipNumber = schoolZipCodeNoBlank.substring(0, 2);
                    if (zipNumber === "97") {
                        zipNumber = schoolZipCodeNoBlank.substring(0, 3);
                    }
                }
                setUserProperty({ key: "school_department", value: zipNumber })
            }
            if (schoolCountry.length > 0) {
                setUserProperty({ key: "school_country", value: schoolCountry })
            }
            if (schoolType.length > 0) {
                setUserProperty({ key: "school_type", value: schoolType })
            }
            setUserProperty({ key: "school_added_manually", value: schoolSelected === true ? "false" : "true" })
            // Si l'utilisateur a accepté de recevoir des e-mails, on l'ajoute à la base
            if (Platform.OS !== "web") {
                addContactToGeneralAudience({
                    email: emailToRecord,
                    userType: CONTEXT.teacher,
                    optIn: newsletter,
                    optInCoach: false,
                    tneEnable
                });
            }
            if ((userSession !== undefined) && (userSession.uid !== undefined)) {
                setIsLoading(false);
                onSignUpConfirmed();
                setStep(3);
            } else {
                setIsLoading(false);
                alertRef.current?.showAlert({
                    title: "Une erreur est survenue",
                    body: "Veuillez réessayer plus tard s'il vous plaît."
                });
            }
        } catch (error) {
            setIsLoading(false);
            let title = "Une erreur est survenue";
            let body = "Veuillez réessayer plus tard s'il vous plaît.\nSi l'erreur persiste, n'hésitez pas à nous contacter : support@lili.cool";
            if (error.toString().indexOf("auth/email-already-in-use") !== -1) {
                title = "Utilisateur existant";
                body = "Votre adresse e-mail est déjà reliée à un compte de LILI.\nSi vous avez oublié votre mot de passe, vous pouvez le retrouver via le lien \"Mot de passe oublié\" dans la page \"DÉJÀ INSCRIT(E)\"";
            } else if (error.toString().indexOf("auth/invalid-email") !== -1) {
                title = "Adresse e-mail erronée";
                body = "Veuillez vérifier votre adresse e-mail s'il vous plaît.";
            } else if (error.toString().indexOf("auth/weak-password") !== -1) {
                title = "Mot de passe trop faible";
                body = "Veuillez saisir au moins 6 caractères pour votre mot de passe.";
            } else if (error.toString().indexOf("auth/network-request-failed") !== -1) {
                title = "Erreur de connexion 📡";
                body = "Vérifiez votre connexion internet pour vous connecter à votre compte s'il vous plaît."
            }
            alertRef.current?.showAlert({
                title,
                body,
                onPressPrimaryButton: () => setStep(0)
            });
        }
    }

    const onFinalStepValidation = () => {
        Navigation.relaunchApp({
            componentId,
            navigate
        });
    }

    const openTneLiliCool = async () => {
        try {
            const tneUrl = "https://tne.lili.cool";
            const canOpenUrl = await Linking.canOpenURL(tneUrl);
            if (canOpenUrl === true) {
                Linking.openURL(tneUrl);
            } else {
                alertRef.current?.showAlert({
                    title: "Une erreur est survenue lors de l'ouverture du lien",
                    body: "Vous pouvez accéder à cette page sur : \n https://tne.lili.cool"
                });
            }
        } catch (error) {
            alertRef.current?.showAlert({
                title: "Une erreur est survenue lors de l'ouverture du lien",
                body: "Vous pouvez accéder à cette page sur : \n https://tne.lili.cool"
            });
        }
        setTimeout(() => {
            onFinalStepValidation();
        }, 500);
    }

    const teacherFourthStep = () => {
        if (step !== 3) {
            return <View />;
        }
        const tneConclusion = false;
        const firstText = "N'oubliez pas de vous inscrire à";
        const secondText = "notre webinar de formation";
        const mainImage = images.illTeacherSignUpFinal;
        const secondImageView = <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View style={{ flex: 1 }}>
                <Text style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.secondaryFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center" }}>
                    {"En vous rendant dans"}
                </Text>
                <Text style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.primaryBoldFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center", textTransform: "uppercase" }}>
                    {"La salle des\nSuper-profs"}
                </Text>
            </View>
            <View style={{ width: appTheme.pixelPerfect(120), height: appTheme.pixelPerfect(145) }}>
                <Image source={images.illTeacherSignUpFinalBis} style={{ height: appTheme.pixelPerfect(145), width: appTheme.pixelPerfect(100) }} resizeMode="contain" />
            </View>
        </View>;
        const discoverLiliButton = <RoundButton textColor={appTheme.white} style={{ backgroundColor: appTheme.schoolColor }} isLoading={isLoading} onPress={onFinalStepValidation} title={"Découvrir Lili"} />;
        return (<View style={{ marginBottom: appTheme.pixelPerfect(10), width: scrollViewWidth, alignItems: "center" }}>
            <Text style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.primaryBoldFont, color: appTheme.schoolColor, fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center", textTransform: "uppercase" }}>
                {"Bienvenue parmi les\nsuper-profs !"}
            </Text>
            <Divider />
            <Text style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.secondaryFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center" }}>
                {firstText}
            </Text>
            <Divider />
            <Text style={{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.primaryBoldFont, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center", textTransform: "uppercase" }}>
                {secondText}
            </Text>
            <Divider />
            <View style={{ height: appTheme.pixelPerfect(180) }}>
                <Image source={mainImage} style={{ height: appTheme.pixelPerfect(180), width: appTheme.pixelPerfect(230) }} resizeMode="contain" />
            </View>
            <Divider />
            {secondImageView}
            <Divider size={20} />
            {discoverLiliButton}
        </View>)
    }

    const teacherThirdStep = () => {
        if (step !== 2) {
            return <View />;
        }
        let choicesForm = [];
        let choices = [
            {
                key: "kindergarten",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.kindergarten")
            },
            {
                key: "elementary",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.elementary")
            },
            {
                key: "middleschool",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.middleschool")
            },
            {
                key: "highschool",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.highschool")
            },
            {
                key: "other",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.other")
            },
            {
                key: "none",
                value: i18n.t("authentication.signUp.form.radioButtons.classes.none")
            }
        ];
        for (const aChoiceIndex in choices) {
            if (Object.prototype.hasOwnProperty.call(choices, aChoiceIndex)) {
                const aChoice = choices[aChoiceIndex];
                choicesForm.push(<RadioButton
                    onPress={() => toggleClasses(aChoice.key)}
                    style={{ width: appTheme.pixelPerfect(220), marginBottom: appTheme.pixelPerfect(10) }}>
                    <Text style={{ flex: 1, fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(10), color: appTheme.darkBlue }}>
                        {aChoice.value}
                    </Text>
                </RadioButton>);
            }
        }
        return (<View style={{ paddingTop: appTheme.pixelPerfect(10), width: scrollViewWidth, alignItems: "center" }}>
            <Text style={[teacherSignUpStyle.titleStyle, { textAlign: "center", color: appTheme.schoolColor, fontFamily: appTheme.primaryBoldFont, paddingTop: appTheme.pixelPerfect(10) }]}>
                {i18n.t("authentication.signUp.form.classesFormTitle").toLocaleUpperCase()}
            </Text>
            <Divider />
            {choicesForm}
            <Divider size={20} />
            <RoundButton isLoading={isLoading} onPress={onThirdStepValidation} title={i18n.t("authentication.signUp.buttons.signUp")} style={{ backgroundColor: appTheme.schoolColor }} />
            <Divider />
            <TouchableOpacity onPress={openGeneralConditions} style={{ marginTop: appTheme.pixelPerfect(20) }}>
                <Text style={{ fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(9), textAlign: "center", color: appTheme.textColor }}>
                    {i18n.t("authentication.signUp.buttons.cgu.firstPart")}
                </Text>
                <Text style={{ fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(9), textAlign: "center", textDecorationLine: "underline", color: appTheme.textColor }}>
                    {i18n.t("authentication.signUp.buttons.cgu.secondPart")}
                </Text>
            </TouchableOpacity>
        </View>);
    }

    const teacherSecondStep = () => {
        if (step !== 1) {
            return <View />;
        }
        return (<View style={{ marginBottom: appTheme.pixelPerfect(10), flex: 1, width: scrollViewWidth, alignItems: "center" }}>
            <Text style={[teacherSignUpStyle.titleStyle, { textAlign: "center", color: appTheme.schoolColor, fontFamily: appTheme.primaryBoldFont, paddingTop: appTheme.pixelPerfect(10) }]}>
                {i18n.t("authentication.signUp.form.schoolFormTitle").toLocaleUpperCase()}
            </Text>
            <Divider />
            <TouchableOpacity onPress={showSchoolSearch} style={[{ alignItems: "center", justifyContent: "space-between", width: appTheme.getBlockWidth() * 0.9, flexDirection: "row", borderWidth: 1, borderColor: appTheme.schoolColor, paddingVertical: appTheme.pixelPerfect(8), paddingHorizontal: appTheme.pixelPerfect(10), minHeight: appTheme.pixelPerfect(30), backgroundColor: appTheme.white, borderRadius: appTheme.pixelPerfect(30) }]}>
                <View style={{ width: appTheme.pixelPerfect(40) }}>
                    <SVGView Component={IcSearch} size={appTheme.pixelPerfect(24)} color={appTheme.schoolColor} />
                </View>
                <Text style={{ flex: 1, color: appTheme.schoolColor, textTransform: "uppercase", fontFamily: appTheme.primarySemiBoldFont, fontSize: appTheme.pixelPerfectForFont(7) }}>
                    {i18n.t("authentication.signUp.form.schoolSearch").toLocaleUpperCase()}
                </Text>
            </TouchableOpacity>
            <Text style={[teacherSignUpStyle.titleStyle, { textAlign: "center", color: appTheme.schoolColor, fontFamily: appTheme.primarySemiBoldFont, paddingHorizontal: appTheme.pixelPerfect(10), paddingTop: appTheme.pixelPerfect(10) }]}>
                {i18n.t("authentication.signUp.form.schoolInputsTitle")}
            </Text>
            <Divider />
            <TextInput
                key="schoolName"
                value={schoolName}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.schoolName !== undefined)) ? errors.schoolName : ""}
                onChangeText={(text) => setSchoolName(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.schoolNamePlaceholder")} />
            <TextInput
                key="schoolAddress"
                value={schoolAddress}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.schoolAddress !== undefined)) ? errors.schoolAddress : ""}
                onChangeText={(text) => setSchoolAddress(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.schoolAddressPlaceholder")} />
            <TextInput
                key="schoolZipCode"
                value={schoolZipCode}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.schoolZipCode !== undefined)) ? errors.schoolZipCode : ""}
                onChangeText={(text) => setSchoolZipCode(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.schoolZipCodePlaceholder")} />
            <TextInput
                key="schoolCity"
                value={schoolCity}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.schoolCity !== undefined)) ? errors.schoolCity : ""}
                onChangeText={(text) => setSchoolCity(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.schoolCityPlaceholder")} />
            <TextInput
                key="schoolCountry"
                value={schoolCountry}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.schoolCountry !== undefined)) ? errors.schoolCountry : ""}
                onChangeText={(text) => setSchoolCountry(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.schoolCountryPlaceholder")} />
            <View style={{ flexDirection: "row", justifyContent: "space-evenly", width: appTheme.getBlockWidth() * 0.7 }}>
                <RadioButton
                    centerButton={true}
                    value={schoolType === "public"}
                    onPress={() => setSchoolType("public")}
                    style={{ flex: 1 }}>
                    <Text style={{ fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(9), color: appTheme.darkBlue }}>
                        {i18n.t("authentication.signUp.form.schoolinstitutionTypePublic")}
                    </Text>
                </RadioButton>
                <RadioButton
                    centerButton={true}
                    value={schoolType === "private"}
                    onPress={() => setSchoolType("private")}
                    style={{ flex: 1 }}>
                    <Text style={{ fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(9), color: appTheme.darkBlue }}>
                        {i18n.t("authentication.signUp.form.schoolinstitutionTypePrivate")}
                    </Text>
                </RadioButton>
            </View>

            <Divider size={30} />
            <RoundButton isLoading={isLoading} onPress={onSecondStepValidation} title={i18n.t("authentication.signUp.buttons.steps")} style={{ backgroundColor: appTheme.schoolColor }} />
        </View>);
    }

    // Première étape
    // Validation du formulaire de la première étape
    const onFirstStepValidation = () => {
        setIsLoading(true);
        let signUpErrors = emptyErrors;
        if (firstname.length === 0) {
            signUpErrors.firstname = i18n.t("authentication.signUp.errors.empty_firstname");
        } else {
            signUpErrors.firstname = "";
        }
        if (lastname.length === 0) {
            signUpErrors.lastname = i18n.t("authentication.signUp.errors.empty_lastname");
        } else {
            signUpErrors.lastname = "";
        }
        if (email.length === 0) {
            signUpErrors.email = i18n.t("authentication.signUp.errors.empty_email");
        } else {
            const isEmailValid = validateEmail(email);
            if (isEmailValid === false) {
                signUpErrors.email = i18n.t("authentication.signUp.errors.wrong_email");
            } else {
                signUpErrors.email = "";
            }
        }
        if (password.length === 0) {
            signUpErrors.password = i18n.t("authentication.signUp.errors.empty_password");
        } else if (password.length < 6) {
            signUpErrors.password = i18n.t("authentication.signUp.errors.wrong_password");
        } else {
            signUpErrors.password = "";
        }
        if ((signUpErrors.email.length > 0) || (signUpErrors.password.length > 0) || (signUpErrors.firstname.length > 0) || (signUpErrors.lastname.length > 0)) {
            // S'il y a des erreurs, on les affiche pour que l'utilisateur les corrige
            setIsLoading(false);
            setErrors(signUpErrors);
        } else {
            // Sans erreur, on demande à l'utilisateur de sélectionner sa fonction
            openTeacherFunctionSelection();
            setIsLoading(false);
        }
    }

    // Récupération des données sélectionnées dans les Action Sheet
    const onActionSheetChange = (item) => {
        if (step === 0) {
            // Selection de la fonction du prof
            selectTeacherFunction(item);
        } else {
            // Selection de l'école
            selectSchool(item);
        }
    }

    // Ouverture de l'ActionSheet pour sélectionner la fonction de l'enseignant
    useEffect(() => {
        if (teacherFunction !== undefined) {
            if (schoolId.length > 0) {
                goToStepAfterSchoolSelection();
            } else {
                setStep(1);
            }

        }
    }, [teacherFunction]);

    const openTeacherFunctionSelection = () => {
        actionSheetRef.current.showAlert({
            actionSheetType: "TEACHER_FUNCTION"
        });
    }

    const selectTeacherFunction = (item) => {
        setTeacherFunction(item.id);
        actionSheetRef.current.hideAlert();
    }

    useEffect(() => {
        
    }, [newsletter]);

    // Prénom / Nom / Email / Mot de passe
    const teacherFirstStep = () => {
        if (step !== 0) {
            return <View />;
        }
        return (<View style={{ paddingTop: appTheme.pixelPerfect(10), width: scrollViewWidth, alignItems: "center" }}>
            <TextInput
                key="firstname"
                value={firstname}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.firstname !== undefined)) ? errors.firstname : ""}
                onChangeText={(text) => setFirstname(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.firstnamePlaceholder")} />
            <TextInput
                key="lastname"
                value={lastname}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.lastname !== undefined)) ? errors.lastname : ""}
                onChangeText={(text) => setLastname(text)}
                type="default"
                autoCapitalize="words"
                placeholder={i18n.t("authentication.signUp.form.lastnamePlaceholder")} />
            <TextInput
                key="email"
                value={email}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.email !== undefined)) ? errors.email : ""}
                onChangeText={(text) => setEmail(text)}
                type="email"
                placeholder={i18n.t("authentication.signUp.form.emailPlaceholder")} />
            <TextInput
                key="password"
                value={password}
                onFocus={() => setErrors(undefined)}
                error={((errors !== undefined) && (errors.password !== undefined)) ? errors.password : ""}
                onChangeText={(text) => setPassword(text)}
                type="password"
                placeholder={i18n.t("authentication.signUp.form.passwordPlaceholder")} />
            <Divider />
            <RadioButton
                buttonBackgroudnColor={appTheme.schoolColor}
                centerButton={false}
                value={newsletter}
                onPress={() => setNewsletter(!newsletter)}
                style={{ width: appTheme.getBlockWidth() * 0.8, backgroundColor: appTheme.schoolColor + "22", borderRadius: appTheme.pixelPerfect(10), padding: appTheme.pixelPerfect(10) }}>
                <Text style={{ fontFamily: appTheme.secondaryFont, fontSize: appTheme.pixelPerfectForFont(9), color: appTheme.darkBlue }}>
                    {i18n.t("authentication.signUp.form.radioButtons.newsletter")}
                </Text>
            </RadioButton>
            <Divider size={30} />
            <RoundButton style={{ backgroundColor: appTheme.schoolColor }} isLoading={isLoading} onPress={onFirstStepValidation} title={i18n.t("authentication.signUp.buttons.steps")} />
            <Divider size={20} />
            <LinkButton onPress={onCancelRequest} title="Annuler" />
        </View>)
    }



    return (<View style={{ justifyContent: "center", alignItems: "center", width: appTheme.getBlockWidth() }}>
        {getStepIndicatorByStep()}
        <Divider />
        <ScrollView scrollEnabled={false} ref={signUpPagerRef} horizontal={true} style={landingStyle.contentContainer}>
            {teacherFirstStep()}
            {teacherSecondStep()}
            {teacherThirdStep()}
            {teacherFourthStep()}
        </ScrollView>
        <Alert ref={alertRef} />
        <ActionSheet ref={actionSheetRef} onChange={onActionSheetChange} />
    </View>)

}

export default TeacherSignUp;